import { useState } from 'react';

import PropTypes from 'prop-types';

import {
    PhotoCameraOutlined,
    SlowMotionVideoOutlined,
    CollectionsOutlined
} from '@mui/icons-material';

import InfoBox from '../../blocks/messages/info-box';
import MediaUploadForm from './components/folder--media--upload-form';

import { useCameraPhoto, useImageProcessing, useVideoRecorder } from '../../../modules/cameraModule'

import { useGallery } from '../../../context/gallery-context'; // Context for gallery preferences
import CommonButton from '../../buttons/common-button';

const UploadSection = ({
    folder,
    inAppointment = false, // Optional prop to indicate if the upload is in an appointment context
}) => {

    // Import the useCameraPhoto hook from the camera module
    const { takePhoto, selectPhoto } = useCameraPhoto();
    const { startRecording } = useVideoRecorder();

    // Import the useGallery hook from the gallery module
    const { addTimestampAndGeolocation, addTimestamp, addGeolocation } = useImageProcessing();
    const { timestamp, geolocation } = useGallery();

    // State variables for geolocation and timestamp options 
    const [geolocationOption, setGeolocationOption] = useState(geolocation);
    const [timestampOption, setTimestampOption] = useState(timestamp);

    // State variables for captured and processed images
    const [mediaLoading, setMediaLoading] = useState(false);
    const [media, setMedia] = useState();

    // State variables for error handling
    const [error, setError] = useState(null);

    /**
     * handleTakePhoto
     * 
     * This asynchronous function is called when the photo button is clicked.
     * It captures an image and processes it based on selected options.
     */
    const handleTakePhoto = async () => {
        try {
            // Set loading to true when starting to take a photo
            setMediaLoading(true); 

            // Capture the photo using the browser's media devices
            const image = await takePhoto();

            // Validate the captured image before updating the state
            if (!image) {
                setError('Aucune image capturée.');
                return;
            }

            // Validate the captured image before updating the state
            if (typeof image !== 'string' || !image.startsWith('data:image/')) {
                setError('Image invalide.');
                return;
            }
                
            // Process the captured image
            const processedImage = await processImage(image);

            // Validate the processed image before updating the state
            if (!processedImage) {
                setError('Erreur lors du traitement de l\'image.');
                return;
            }

            // Update the state with the processed image and type
            setMedia({
                file: processedImage,
                type: 'image',
            });

            // Reset the error message
            setError(null);
        } catch (error) {
            setError(error?.message || 'Une erreur inattendue est survenue');
        } finally {
            setMediaLoading(false);
        }
    };

    /**
     * handleSelectPhoto
     * 
     * This asynchronous function is called when the select photo button is clicked.
     * It allows the user to choose an image from their device and processes it based on selected options.
     */
    const handleSelectPhoto = async () => {
        try {
            // Set loading to true when starting to select a photo
            setMediaLoading(true);

            // Select photo with preferences
            const image = await selectPhoto();

            // Validate the captured image before updating the state
            if (!image) {
                setError('Aucune image capturée.');
                return;
            }

            // Validate the captured image before updating the state
            if (typeof image !== 'string' || !image.startsWith('data:image/')) {
                setError('Image invalide.');
                return;
            }

            // Process the captured image
            const processedImage = await processImage(image);

            // Validate the processed image before updating the state
            if (!processedImage) {
                setError('Erreur lors du traitement de l\'image.');
                return;
            }
             
            // Update the state with the processed image and type
            setMedia({
                file: processedImage,
                type: 'image',
            });

            // Reset the error message
            setError(null);
        } catch (error) {
            setError(error?.message || 'Une erreur inattendue est survenue');
        } finally {
            setMediaLoading(false);
        }
    };

    /**
     * handleRecordVideo
     * 
     * This asynchronous function is called when the record video button is clicked.
     * It allows the user to record a video using the device's camera and processes it.
     */
    const handleRecordVideo = async () => {
        try {
            // Set loading to true when starting to record a video
            setMediaLoading(true);

            // Start recording
            const base64video = await startRecording();

            // Validate the recorded video before updating the state
            if (!base64video) {
                setError('Aucune vidéo capturée.');
                return;
            }

            // Update the state with the processed image and type
            setMedia({
                file: base64video,
                type: 'video',
            });

            // Reset the error message
            setError(null);
        } catch (error) {
            setError(error?.message || 'Une erreur inattendue est survenue');
        } finally {
            setMediaLoading(false);
        }
    };

    /**
     * processImage
     * 
     * This function processes the captured image based on the selected
     * options for timestamp and geolocation.
     * 
     * @param {string} image - The captured image to process.
     * @returns {Promise<string>} - The processed image with added timestamp and/or geolocation.
     */
    const processImage = async (image) => {

        // Check if the image is missing
        if (!image) return null;

        try {
            // Process the image based on user preferences
            if (timestampOption && geolocationOption) {

                // Add timestamp and geolocation
                return await addTimestampAndGeolocation(image);
            } else {
                if (timestampOption) {
                    // Add timestamp only
                    return await addTimestamp(image);
                }
                if (geolocationOption) {
                    // Add geolocation only
                    return await addGeolocation(image); 
                }
            }
        } catch (error) {
            setError(error?.message || 'Erreur lors du traitement de l\'image.');
        }
    };

    /**
     * handleDeleteMedia
     * 
     * This function is called when the delete button is clicked.
     * It removes the media from the state.
     * 
     * @returns {void}
     */
    const handleDeleteMedia = () => {
        setMedia(null);
    };

    // Render the component
    return (
        <div className='media-upload--section'>
            { /* Display the error message if an error occurs */ }
            { error &&
                <InfoBox
                    type="error"
                    text={error}
                    onRemove={() => setError(null)}
                />
            }

            {/* Display the error message if an error occurs */}
            <div className='media-tools--manager w-50'>
                { !media ? (
                    <div className="media-tools-bar">
                        <h2>Prendre ou sélectionner un média</h2>
                        <CommonButton
                            icon={<PhotoCameraOutlined />} // Pass the icon as JSX
                            label="Prendre une photo"
                            onClick={handleTakePhoto} // Call function on button click
                            mobileStickerEnabled={true} // Add mobile sticker functionality
                            className="btn-take-photo"
                        />
                        <CommonButton
                            icon={<SlowMotionVideoOutlined />} // Pass the icon as JSX
                            label="Prendre une vidéo"
                            onClick={handleRecordVideo} // Call function on button click
                            mobileStickerEnabled={true} // Add mobile sticker functionality
                            className="btn-take-video"
                        />
                        <CommonButton
                            icon={<CollectionsOutlined />} // Pass the icon as JSX
                            label="Ouvrir la galerie du téléphone"
                            onClick={handleSelectPhoto} // Call function on button click
                            mobileStickerEnabled={true} // Add mobile sticker functionality
                            className="btn-open-gallery"
                        />
                    </div>
                ) : (
                    <div className="single-media-preview">
                        {/* Display the delete button */}
                        <button
                            className="media-preview--delete"
                            onClick={() => handleDeleteMedia()}
                        >
                            ✕
                        </button>

                        {/* Display the media preview */}
                        {media.type === 'video' ? (
                            <video
                                controls
                                className="media-preview"
                                aria-label="Preview de la vidéo capturée"
                            >
                                <source src={media.file} type="video/mp4" />
                                Votre navigateur ne supporte pas la balise vidéo.
                            </video>
                        ) : media.type === 'image' ? (
                            <img
                                src={media.file}
                                alt="Captured media"
                                className="media-preview"
                                aria-label="Preview de l'image capturée"
                            />
                        ) : (
                            <span>Type de média non pris en charge</span>
                        )}
                    </div>
                )}

                {mediaLoading && <span>Media en cours de traitement...</span>}
            </div>

            {/* Display the form with the fields and submit buttons */}
            <MediaUploadForm
                media={media}
                setMedia={(media) => setMedia(media)} 
                folder={folder}
                geolocation={geolocationOption}
                timestamp={timestampOption}
                onGeolocationChange={() => setGeolocationOption(!geolocationOption)}
                onTimestampChange={() => setTimestampOption(!timestampOption)}
                inAppointment={inAppointment}
            />
        </div>
    );
};

// Prop types for the UploadSection component
UploadSection.propTypes = {
    folder: PropTypes.object.isRequired,
};

export default UploadSection;
